import React, { useEffect, useRef, useState } from 'react';

import { containsProfanity } from '../utils/ProfanityFilter';

interface PlayerNameModalProps {
  onSubmit: (name: string) => void;
  onCancel: () => void;
  score: number;
}

export const PlayerNameModal: React.FC<PlayerNameModalProps> = ({
  onSubmit,
  onCancel,
  score,
}) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Add a mechanism to prevent game controls from affecting the game while typing
  useEffect(() => {
    // Function to stop propagation of keydown events for WASD, R, F, and Space
    const handleKeyDown = (e: KeyboardEvent) => {
      // Game control keys we need to prevent from affecting the game
      const gameControlKeys = ['w', 'a', 's', 'd', 'r', 'f', ' '];

      // Check if the keypress is for a game control key
      if (gameControlKeys.includes(e.key.toLowerCase())) {
        // Don't stop default behavior (typing), just stop propagation to prevent game actions
        e.stopPropagation();
      }

      // Prevent Escape from closing the modal (it's used to exit missions)
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Get the input element
    const inputElement = inputRef.current;

    // Add event listener to input field
    if (inputElement) {
      inputElement.addEventListener('keydown', handleKeyDown, true);
    }

    // Global handler to catch any missed events
    document.addEventListener('keydown', handleKeyDown, true);

    // Clean up event listeners on unmount
    return () => {
      if (inputElement) {
        inputElement.removeEventListener('keydown', handleKeyDown, true);
      }
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  // Check name for profanity whenever it changes
  useEffect(() => {
    if (name && containsProfanity(name)) {
      setError('Please use appropriate language for your name');
    } else {
      // Clear the profanity error if it exists
      if (error === 'Please use appropriate language for your name') {
        setError(null);
      }
    }
  }, [name, error]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }

    if (name.length > 15) {
      setError('Name must be 15 characters or less');
      return;
    }

    // Check for profanity before submitting
    if (containsProfanity(name)) {
      setError('Please use appropriate language for your name');
      return;
    }

    onSubmit(name.trim());
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
    backdropFilter: 'blur(5px)',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: 'rgba(26, 26, 46, 0.9)',
    padding: '20px',
    borderRadius: '10px',
    width: '90%',
    maxWidth: '400px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  };

  const titleStyle: React.CSSProperties = {
    color: 'white',
    fontSize: '24px',
    marginBottom: '10px',
    textAlign: 'center',
  };

  const subtitleStyle: React.CSSProperties = {
    color: '#FFD700',
    fontSize: '18px',
    marginBottom: '20px',
    textAlign: 'center',
  };

  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  };

  const inputStyle: React.CSSProperties = {
    padding: '12px 15px',
    borderRadius: '5px',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    fontSize: '16px',
    outline: 'none',
  };

  const errorStyle: React.CSSProperties = {
    color: '#FF4040',
    fontSize: '14px',
    marginTop: '5px',
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '20px',
    gap: '10px',
  };

  const buttonBaseStyle: React.CSSProperties = {
    padding: '10px 15px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    border: 'none',
    flex: 1,
    transition: 'all 0.2s ease',
  };

  const submitButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: '#FF6600',
    color: 'white',
  };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => e.stopPropagation()}
      data-modal-active="true"
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>Congratulations!</h2>
        <p style={subtitleStyle}>You scored {score} points!</p>
        <form onSubmit={handleSubmit} style={formStyle}>
          <div>
            <input
              ref={inputRef}
              type="text"
              placeholder="Enter your name"
              style={inputStyle}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              maxLength={15}
              onClick={(e) => e.stopPropagation()}
            />
            {error && <div style={errorStyle}>{error}</div>}
          </div>
          <div style={buttonContainerStyle}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
              style={cancelButtonStyle}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={(e) => e.stopPropagation()}
              style={submitButtonStyle}
              disabled={!!error}
            >
              Submit Score
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
